import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/utils/apiClient";
import { useAuth } from "@/state/auth";

export type CostStatus = "measured" | "approximate" | "not_instrumented";

export interface CostFeature {
  feature: string;
  label: string;
  status: CostStatus;
  cost_usd: number;
  note: string;
}

export interface CostModel {
  model: string;
  kind: string;
  generations: number;
  tokens_prompt: number;
  tokens_output: number;
  cost_usd: number;
  priced: boolean;
}

export interface CostContentType {
  content_type: string;
  generations: number;
  tokens_prompt: number;
  tokens_output: number;
  cost_usd: number;
}

export interface CostSummary {
  currency: string;
  range: { from: string; to: string };
  total_measured_usd: number;
  text_cost_usd: number;
  voice_cost_usd: number;
  chat_cost_usd?: number;
  embedding_cost_usd?: number;
  not_instrumented: string[];
  totals: {
    generations: number;
    tokens_prompt: number;
    tokens_output: number;
    chat_turns?: number;
    chat_tokens_prompt?: number;
    chat_tokens_output?: number;
    embedding_calls?: number;
    embedding_tokens?: number;
    voice_chars_est: number;
    voice_clips: number;
  };
  by_feature: CostFeature[];
  by_model: CostModel[];
  by_content_type: CostContentType[];
  missing_pricing: string[];
}

export interface PricingRow {
  model_key: string;
  kind: string;
  unit: string;
  input_usd: number;
  output_usd: number;
  label: string | null;
  updated_at: string | null;
  updated_by: string | null;
}

export const useAiCostSummary = (from?: string, to?: string) => {
  const profile = useAuth((s) => s.profile);
  const params = new URLSearchParams();
  if (from) params.set("from", from);
  if (to) params.set("to", to);
  const qs = params.toString();
  return useQuery({
    queryKey: ["admin", "ai-cost", "summary", from ?? "", to ?? ""],
    queryFn: async () => {
      const { summary } = await apiRequest<{ summary: CostSummary }>(
        `/api/admin/ai-cost/summary${qs ? `?${qs}` : ""}`,
      );
      return summary;
    },
    staleTime: 60_000,
    enabled: Boolean(profile?.isSuperadmin),
  });
};

export const useAiCostPricing = () => {
  const profile = useAuth((s) => s.profile);
  return useQuery({
    queryKey: ["admin", "ai-cost", "pricing"],
    queryFn: async () => {
      const { pricing } = await apiRequest<{ pricing: PricingRow[] }>(
        "/api/admin/ai-cost/pricing",
      );
      return pricing;
    },
    staleTime: 300_000,
    enabled: Boolean(profile?.isSuperadmin),
  });
};

export const useUpdatePricing = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { model_key: string; input_usd: number; output_usd: number }) => {
      const { pricing } = await apiRequest<{ pricing: PricingRow }>(
        `/api/admin/ai-cost/pricing/${encodeURIComponent(input.model_key)}`,
        {
          method: "PUT",
          body: JSON.stringify({ input_usd: input.input_usd, output_usd: input.output_usd }),
        },
      );
      return pricing;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "ai-cost", "pricing"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "ai-cost", "summary"] });
    },
  });
};
